import { Component, HostListener, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarComponent } from '../sidebar/sidebar';
import { TabBarComponent } from '../tab-bar/tab-bar';
import { NavService } from '../../core/services/nav.service';
import { TabService } from '../../core/services/tab.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, TabBarComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './shell.html',
})
export class ShellComponent {
  private router = inject(Router);
  private navService = inject(NavService);
  private tabService = inject(TabService);
  private authService = inject(AuthService);

  /** Live viewport width — drives mobile detection */
  readonly screenWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);

  /** Treat anything < 768 px as mobile */
  readonly isMobile = computed(() => this.screenWidth() < 768);

  /** Whether the sidebar drawer is open (mobile only) */
  readonly drawerOpen = signal(false);
  /** Sidebar collapsed state for desktop (icon-only rail); starts collapsed until user expands. */
  readonly sidebarCollapsed = signal(true);

  @HostListener('window:resize')
  onResize(): void {
    this.screenWidth.set(window.innerWidth);
    // Auto-close drawer when resizing back to desktop
    if (window.innerWidth >= 768) this.drawerOpen.set(false);
  }

  constructor() {
    if (this.authService.isAuthenticated()) {
      this.authService.decodeUser();
      this.authService.fetchUserProfile().subscribe();
    }

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => {
        const url = (event as NavigationEnd).urlAfterRedirects.split('?')[0];
        const item = this.navService.findItemByRoute(url);
        if (item?.route) {
          this.tabService.openTab({ id: item.id, title: item.label, route: item.route });
        }
      });
  }

  toggleSidebarCollapse(): void {
    if (this.isMobile()) return;
    this.sidebarCollapsed.update((v) => !v);
  }
}
